// @ts-nocheck
import {
  getLiveForms,
  getTicketFormFieldMap,
} from "../../services/helpers/QueryBuilderHelper";
import { FormSubmittedProperty } from "../../constants/customQueryBuilderValues";
import {
  FetchCustomFieldResponseDataType,
  FetchCustomFieldResponseFieldType,
} from "../../admin/models/campaign/fetchCustomFieldsModel";

/**
 * QB connector type strings (QueryBuilderConnectorType enum values from
 * @bikdotai/bik-component-library). These drive operator sets and value
 * input components — see docs/triggers/event-triggers.md for the full map.
 */
export enum QBConnectorType {
  String = "string",
  StringRestrictedIsOnly = "string_restricted_3",
  Boolean = "boolean",
  Date = "date",
  DateDaysOnly = "dateDaysOnly",
  Integer = "integer",
}

/** Keyed by formId string. Populated on first fetch and reused across calls. */
export const formFieldCache: Record<string, any[]> = {};

export const fieldTypeToQBType = (fieldType: string): QBConnectorType => {
  switch (fieldType as FetchCustomFieldResponseFieldType) {
    case FetchCustomFieldResponseFieldType.DROPDOWN:
      return QBConnectorType.StringRestrictedIsOnly;
    case FetchCustomFieldResponseFieldType.MULTISELECT_DROPDOWN:
      return QBConnectorType.String;
    case FetchCustomFieldResponseFieldType.CHECKBOX:
    case FetchCustomFieldResponseFieldType.ATTACHMENT:
      return QBConnectorType.Boolean;
    case FetchCustomFieldResponseFieldType.DATE:
    case FetchCustomFieldResponseFieldType.DATE_TIME:
      return QBConnectorType.DateDaysOnly;
    case FetchCustomFieldResponseFieldType.NUMBER:
    case FetchCustomFieldResponseFieldType.INTEGER:
      return QBConnectorType.Integer;
    default:
      return QBConnectorType.String;
  }
};

/**
 * Maps ticket field types to variable type strings used in the variable picker.
 * Mirrors CustomFieldHelper.getDataTypeForVariable() — uses the same
 * FetchCustomFieldResponseFieldType / FetchCustomFieldResponseDataType enums.
 */
export const fieldTypeToVariableType = (fieldType: string): string => {
  const ft = fieldType as FetchCustomFieldResponseFieldType;
  // Date fields — preserve full precision ("date" vs "date_time")
  if (ft === FetchCustomFieldResponseFieldType.DATE || ft === FetchCustomFieldResponseFieldType.DATE_TIME) {
    return fieldType;
  }
  // Dropdown — options-based variable
  if (ft === FetchCustomFieldResponseFieldType.DROPDOWN) {
    return fieldType;
  }
  // Remaining types — return the equivalent dataType string
  switch (ft) {
    case FetchCustomFieldResponseFieldType.CHECKBOX:
      return FetchCustomFieldResponseDataType.BOOLEAN;
    case FetchCustomFieldResponseFieldType.NUMBER:
    case FetchCustomFieldResponseFieldType.INTEGER:
      return FetchCustomFieldResponseDataType.NUMBER;
    case FetchCustomFieldResponseFieldType.MULTISELECT_DROPDOWN:
    case FetchCustomFieldResponseFieldType.ATTACHMENT:
      return FetchCustomFieldResponseDataType.STRING_ARRAY;
    default:
      // SINGLE_LINE, MULTI_LINE, unknown
      return FetchCustomFieldResponseDataType.STRING;
  }
};

/** Extracts the form ID from the various shapes the QB library stores values in. */
export const resolveFormId = (rawValue: any): string | null => {
  const id = Array.isArray(rawValue)
    ? (rawValue[0]?.id ?? rawValue[0])
    : typeof rawValue === "object"
    ? rawValue?.id
    : rawValue;
  return id ? String(id) : null;
};

const FORM_ID_NAME_OPTIONS = [
  {
    id: FormSubmittedProperty.FormId,
    name: "Form Id",
    type: QBConnectorType.StringRestrictedIsOnly,
  },
  {
    id: FormSubmittedProperty.FormName,
    name: "Form Name",
    type: QBConnectorType.StringRestrictedIsOnly,
  },
];

/**
 * Handles `{ triggerName: "formSubmitted" }` (property names / "where" field selector).
 *
 * Returns `{ data, status, variableList }` where `variableList` must be assigned
 * back to the module-level variable in the caller.
 */
export const getFormProperties = async (
  storeId: string,
  existingProperties: any[]
): Promise<{ data: any[]; status: number; variableList?: string[] }> => {
  const selectedFormProp = existingProperties.find(
    (p: any) =>
      (p.name === FormSubmittedProperty.FormId ||
        p.name === FormSubmittedProperty.FormName) &&
      p.value
  );

  if (!selectedFormProp?.value) {
    return { data: FORM_ID_NAME_OPTIONS, status: 200 };
  }

  const resolvedFormId = resolveFormId(selectedFormProp.value);
  if (!resolvedFormId) {
    return { data: FORM_ID_NAME_OPTIONS, status: 200 };
  }

  const fieldsResponse = await getTicketFormFieldMap(storeId, resolvedFormId);
  if (!fieldsResponse.data) {
    return { data: FORM_ID_NAME_OPTIONS, status: 200 };
  }

  const rawFields = fieldsResponse.data?.data?.fieldMap || [];
  formFieldCache[resolvedFormId] = rawFields;

  const fields = rawFields.map((f: any) => ({
    id: f.ticket_field?.alias || f.ticket_field?.name || `Field ${f.fieldId}`,
    name: f.ticket_field?.name || `Field ${f.fieldId}`,
    type: fieldTypeToQBType(f.ticket_field?.fieldType),
  }));

  const variableList = [
    FormSubmittedProperty.FormId,
    FormSubmittedProperty.FormName,
    ...fields.map((f: any) => f.id),
  ];

  return {
    data: [...FORM_ID_NAME_OPTIONS, ...fields],
    status: 200,
    variableList,
  };
};

/**
 * Handles `{ triggerName: "formSubmitted", propertyName, searchKey? }` (value dropdown).
 *
 * Returns `{ data, status }` or `null` if the propertyName is not a formSubmitted property.
 */
export const getFormFieldValues = async (
  storeId: string,
  propertyName: string,
  searchKey?: string
): Promise<{ data: any[]; status: number } | null> => {
  // formId / formName → live form picker
  if (
    propertyName === FormSubmittedProperty.FormId ||
    propertyName === FormSubmittedProperty.FormName
  ) {
    const response = await getLiveForms(storeId);
    if (!response.data) return { data: [], status: 200 };

    const forms: any[] = response.data?.data?.ticketForms || [];
    const isFormId = propertyName === FormSubmittedProperty.FormId;

    const filtered = searchKey
      ? forms.filter((form) =>
          (isFormId ? String(form.id) : form.name)
            .toLowerCase()
            .includes(searchKey.toLowerCase())
        )
      : forms;

    return {
      data: filtered.map((form) => ({
        id: String(form.id),
        name: isFormId ? String(form.id) : form.name,
      })),
      status: 200,
    };
  }

  // form field property → options from cache
  const allCachedFields = Object.values(formFieldCache).flat();
  const matchingField = allCachedFields.find(
    (f: any) =>
      (f.ticket_field?.alias || f.ticket_field?.name || String(f.ticket_field?.id)) === propertyName
  );

  if (matchingField) {
    const fieldType = matchingField.ticket_field?.fieldType;
    if (
      fieldType === FetchCustomFieldResponseFieldType.DROPDOWN ||
      fieldType === FetchCustomFieldResponseFieldType.MULTISELECT_DROPDOWN
    ) {
      const options: string[] =
        matchingField.ticket_field?.extraInformation?.options || [];
      const filtered = searchKey
        ? options.filter((opt) =>
            opt.toLowerCase().includes(searchKey.toLowerCase())
          )
        : options;
      return {
        data: filtered.map((opt) => ({ id: opt, name: opt })),
        status: 200,
      };
    }
  }

  return { data: [], status: 200 };
};
